import { Box } from "arkh";
import React, { useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import {
  TodoToolbarItem,
  ToolbarFeature,
  TodoToolbarItemFeature,
} from "widgets/src/Todo/TodoToolbar/TodoToolbarItem";
import { TodoToolbarEdit_todo$key } from "./__generated__/TodoToolbarEdit_todo.graphql";
import { notEmpty } from "../../utils";
import { TodoToolbarContainer } from "./TodoToolbarButton";

function sortOrdinal(a: "hidden" | "locked-active" | "active" | "inactive") {
  switch (a) {
    case "hidden":
      return 0;
    case "locked-active":
      return 1;
    case "active":
      return 2;
    case "inactive":
      return 3;
  }
}
export const TodoToolbarEdit = React.memo(TodoToolbarEditC);
function TodoToolbarEditC(props: {
  todo: TodoToolbarEdit_todo$key;
  editableFeatures?: readonly ToolbarFeature[];
  openFeature: (feature: ToolbarFeature) => void;
  currentEmployeeId?: string;
}) {
  const todo = useFragment(
    graphql`
      fragment TodoToolbarEdit_todo on Todo {
        isOrg

        expireAtDate {
          isoString
          storeFormattedDate(style: DEFAULT)
          __typename
        }
        assigned {
          employee {
            ...ToolbarButtonAssignees_employees
          }
          __typename
        }
        assignedRoles {
          role {
            ...ToolbarButtonAssignees_roles
          }
          __typename
        }
        tags {
          ...TodoToolbarButtonTags_tags
          __typename
        }
        teams {
          ...TodoToolbarButtonTeams_teams
        }

        category {
          tags {
            __typename
          }
        }
        priority
        canWrite
      }
    `,
    props.todo
  );

  const deadline = useMemo(() => {
    if (todo.isOrg) {
      return todo.expireAtDate
        ? ("locked-active" as const)
        : ("hidden" as const);
    }
    return todo.expireAtDate ? ("active" as const) : ("inactive" as const);
  }, [todo.expireAtDate, todo.isOrg]);
  const priority = useMemo(() => {
    if (todo.isOrg) {
      return todo.priority && todo.priority !== "DEFAULT"
        ? ("locked-active" as const)
        : ("hidden" as const);
    }
    return todo.priority && todo.priority !== "DEFAULT"
      ? ("active" as const)
      : ("inactive" as const);
  }, [todo.priority, todo.isOrg]);

  const hasTags = (todo.category?.tags.length ?? 0) > 0;

  const features = useMemo((): (TodoToolbarItemFeature & {
    mode: "hidden" | "locked-active" | "active" | "inactive";
  })[] => {
    return [
      {
        feature: "TEAM" as const,
        mode: todo.isOrg ? ("hidden" as const) : ("active" as const),
        teams: todo.teams,
      },

      { feature: "PRIORITY" as const, mode: priority, priority: todo.priority },
      {
        feature: "DEADLINE" as const,
        mode: deadline,
        expireAt: todo.expireAtDate?.storeFormattedDate
          ? {
              date: new Date(todo.expireAtDate.isoString),
              formattedDate: todo.expireAtDate.storeFormattedDate,
            }
          : undefined,
      },
      {
        feature: "ASSIGNEE" as const,
        assignedEmployees: todo.assigned
          .map((e) => e.employee)
          .filter(notEmpty),
        assignedRoles: todo.assignedRoles
          .map((r) => r.role)
          .filter(notEmpty),
        mode:
          todo.assigned.length > 0 || todo.assignedRoles.length > 0
            ? ("active" as const)
            : ("inactive" as const),
      },
      {
        feature: "TAG" as const,
        tags: todo.tags,
        mode: hasTags
          ? todo.tags.length > 0
            ? ("active" as const)
            : ("inactive" as const)
          : ("hidden" as const),
      },
    ]

      .filter((f) => {
        if (f.mode === "hidden") {
          return false;
        }
        if (
          props.editableFeatures &&
          f.mode === "inactive" &&
          !props.editableFeatures.includes(f.feature)
        ) {
          return false;
        }
        return true;
      })
      .sort((a, b) => sortOrdinal(a.mode) - sortOrdinal(b.mode));
  }, [
    todo.isOrg,
    todo.teams,
    todo.priority,
    todo.expireAtDate,
    todo.assigned,
    todo.assignedRoles,
    todo.tags,
    priority,
    deadline,
    hasTags,
    props.editableFeatures,
  ]);

  const lockedFeatures = useMemo(() => {
    return features.filter((f) => f.mode === "locked-active");
  }, [features]);
  const activeFeatures = useMemo(() => {
    return features.filter((f) => f.mode !== "locked-active");
  }, [features]);
  const isReadOnly = !todo.canWrite;

  return (
    <Box space="medium" spaceTop="none">
      <TodoToolbarContainer
        locked={
          lockedFeatures.length > 0 ? (
            <>
              {lockedFeatures.map((feature) => (
                <TodoToolbarItem
                  key={feature.feature}
                  feature={feature}
                  onPress={props.openFeature}
                  mode={feature.mode}
                  isReadOnly={isReadOnly}
                  currentEmployeeId={props.currentEmployeeId ?? undefined}
                />
              ))}
            </>
          ) : null
        }
        editable={
          activeFeatures.length > 0 ? (
            <>
              {activeFeatures.map((feature) => (
                <TodoToolbarItem
                  key={feature.feature}
                  feature={feature}
                  onPress={props.openFeature}
                  mode={feature.mode}
                  isReadOnly={
                    isReadOnly ||
                    (props.editableFeatures
                      ? !props.editableFeatures.includes(feature.feature)
                      : false)
                  }
                  currentEmployeeId={props.currentEmployeeId ?? undefined}
                />
              ))}
            </>
          ) : null
        }
      />
    </Box>
  );
}
